import { Link } from '@sortlist-frontend/design-system';
import { Fragment } from 'react';

import { RankingAuthorCard } from '_features/RankingPage/components/RankingAuthorCard';

import { GuideBlocks } from './components/GuideBlocks';
import { GuideFaq } from './components/GuideFaq';
import { GuideHeader } from './components/GuideHeader';
import { GuideKeyPoints } from './components/GuideKeyPoints';
import { GuideReadingProgress } from './components/GuideReadingProgress';
import { GuideShortAnswer } from './components/GuideShortAnswer';
import { GuideStickyCta } from './components/GuideStickyCta';
import { GuideToc } from './components/GuideToc';
import type { GuidePageContent } from './types';

interface GuidePageProps {
  content: GuidePageContent;
}

export const GuidePage = (props: GuidePageProps) => {
  const { content } = props;
  const { sections, locale, path, labels } = content;
  const serviceName = content.about ?? content.header.chips[0];

  return (
    <Fragment>
      <GuideReadingProgress label={content.labels.readingProgress} />
      <div className="mx-auto w-full max-w-6xl px-4 pb-24 md:px-6">
        <GuideHeader content={content} />
        <div className="mt-8">
          <GuideShortAnswer section={content.shortAnswer} />
        </div>
        <div className="mt-12">
          <GuideKeyPoints section={content.keyPoints} />
        </div>
        <div className="mt-12 lg:grid lg:grid-cols-[16rem_1fr] lg:gap-10">
          <aside className="hidden lg:block">
            {content.toc != null ? <GuideToc toc={content.toc} cta={content.cta} /> : null}
          </aside>
          <div className="min-w-0 space-y-16">
            {sections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                className="scroll-mt-24"
                aria-labelledby={`${section.id}-title`}>
                <h2 id={`${section.id}-title`} className="mb-6 text-2xl font-semibold text-secondary-950 md:text-3xl">
                  {section.title}
                </h2>
                <GuideBlocks
                  blocks={section.blocks}
                  locale={locale}
                  serviceName={serviceName}
                  path={path}
                  labels={labels}
                />
              </section>
            ))}
            <GuideFaq section={content.faq} />
            <section id="author" className="scroll-mt-24" aria-labelledby="author-title">
              <h2 id="author-title" className="mb-6 text-2xl font-semibold text-secondary-950">
                {content.authorTitle}
              </h2>
              <RankingAuthorCard author={content.author} />
            </section>
            <section aria-labelledby="related-title">
              <h2 id="related-title" className="mb-4 text-xl font-semibold text-secondary-950">
                {content.related.title}
              </h2>
              <ul className="flex flex-wrap gap-2 text-sm">
                {content.related.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      color="foreground"
                      underline="hover"
                      className="rounded-full border border-secondary-200 bg-white px-3 py-1.5 text-sm">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      </div>
      <GuideStickyCta cta={content.cta} />
    </Fragment>
  );
};
